import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import startCase from 'lodash/startCase'
import {
  Button,
  HStack,
  IconButton,
  Input,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'
import { RENTAL_STATUS } from '@homevest/utils/rentals'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { OccupantsFragment, TenantsFragment, useUserListQuery } from 'graphql/generated'
import axios from 'lib/axios'
import { hasCapability } from 'lib/admin-perms'
import { getContactInfo } from 'lib/users'
import { StoreState } from 'store'
import { buildAddTenantSearchQueryArgs } from './addTenantSearch'

interface NormalizedOccupant {
  id: string | null
  name: string | null
  age: string | null
  relation: string | null
  occupant_number: number | null
}

type TenantsRental = TenantsFragment & OccupantsFragment
type OnRefresh = () => void | Promise<void>

function AddTenantModal({
  isOpen,
  onClose,
  rentalId,
  onRefresh
}: {
  isOpen: boolean
  onClose: () => void
  rentalId: string
  onRefresh: OnRefresh
}) {
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [savingUserId, setSavingUserId] = useState<string | null>(null)
  const [errorText, setErrorText] = useState('')
  const updateDebouncedSearchText = useMemo(
    () => debounce(setDebouncedSearchText, 250, { trailing: true }),
    []
  )

  useEffect(() => () => updateDebouncedSearchText.cancel(), [updateDebouncedSearchText])

  const searchQueryArgs = buildAddTenantSearchQueryArgs(debouncedSearchText)
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching }] = useUserListQuery({
    ...searchQueryArgs,
    context: queryContext
  })
  const users = searchQueryArgs.pause ? [] : data?.users ?? []

  const addTenant = async (userId: string) => {
    setSavingUserId(userId)
    setErrorText('')

    try {
      await axios.post(`/admin/rentals/${rentalId}/users`, { user_id: userId })
      await onRefresh()
      setSearchText('')
      setDebouncedSearchText('')
      onClose()
    } catch (err: any) {
      setErrorText(err.message)
    } finally {
      setSavingUserId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Tenant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            value={searchText}
            placeholder='Search users'
            onChange={(event) => {
              setSearchText(event.target.value)
              updateDebouncedSearchText(event.target.value)
            }}
          />
          {errorText && (
            <Text color='red.500' fontSize='sm' mt={2}>
              {errorText}
            </Text>
          )}
          {fetching && <Spinner size='sm' mt={3} />}
          <List mt={3} spacing={2}>
            {users.map((user) => (
              <ListItem key={user.id}>
                <HStack justify='space-between' align='center'>
                  <VStack align='flex-start' spacing={0}>
                    <Text fontWeight='medium'>
                      {user.first_name} {user.last_name}
                    </Text>
                    <Text fontSize='xs' color='gray.600'>
                      {getContactInfo('email', user.user_contact_details)}{' '}
                      {getContactInfo('phone', user.user_contact_details)}
                    </Text>
                  </VStack>
                  <Button
                    size='xs'
                    colorScheme='teal'
                    isLoading={savingUserId === user.id}
                    onClick={() => addTenant(user.id)}
                  >
                    Add
                  </Button>
                </HStack>
              </ListItem>
            ))}
          </List>
        </ModalBody>
        <ModalFooter>
          <Button size='sm' variant='ghost' onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default function Tenants({
  rental,
  onRefresh
}: {
  rental: TenantsRental
  onRefresh?: OnRefresh
}) {
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const isCxAdmin = hasCapability(admin, CAPABILITY_TYPES.CX_ADMIN)
  const canManageTenants = isCxAdmin && !!onRefresh && rental.status === RENTAL_STATUS.ACTIVE
  const canAddTenant = canManageTenants && !!rental.buildium_lease_id
  const [isAddTenantModalOpen, setIsAddTenantModalOpen] = useState(false)
  const [removingRentalUserId, setRemovingRentalUserId] = useState<string | null>(null)
  const [promotingRentalUserId, setPromotingRentalUserId] = useState<string | null>(null)

  type RentalUser = TenantsRental['rental_users'][number]
  function isRentalUser(tenant: any): tenant is RentalUser {
    return 'role' in tenant
  }
  function isOccupant(tenant: any): tenant is NormalizedOccupant {
    return 'occupant_number' in tenant && 'name' in tenant
  }

  function copyData(tenantData: Array<RentalUser | NormalizedOccupant>) {
    const formattedTenants = tenantData
      .filter((t) => {
        if (isRentalUser(t)) return t.deactivated_at === null
        else if (isOccupant(t)) return true
        else return false
      })
      .map((t) => {
        let copyString = ''
        if (isRentalUser(t)) {
          const { user, role } = t
          copyString = `${user?.first_name} ${user?.last_name} - ${startCase(
            role
          )} - ${getContactInfo('email', user?.user_contact_details)} - ${getContactInfo(
            'phone',
            user?.user_contact_details
          )}`
        } else if (isOccupant(t)) {
          copyString = `${t.name} - Occupant #${t.occupant_number} - ${t.relation}`
        }
        return copyString
      })
      .join('\n')
    navigator.clipboard.writeText(formattedTenants)
  }

  const sortedRentalUsers = [...rental.rental_users].sort((a, b) => {
    // If comparing active vs. deactivated, deactivated should come later
    if (a.deactivated_at && !b.deactivated_at) {
      return 1
    }
    if (!a.deactivated_at && b.deactivated_at) {
      return -1
    }
    // Then, list primary tenants first and all others after
    if (a.role === 'primary' && b.role !== 'primary') {
      return -1
    }
    if (b.role === 'primary' && a.role !== 'primary') {
      return 1
    }
    // Then, go alphabetically
    const aName = `${a.user?.first_name ?? ''} ${a.user?.last_name ?? ''}`.trim()
    const bName = `${b.user?.first_name ?? ''} ${b.user?.last_name ?? ''}`.trim()
    return aName.localeCompare(bName)
  })
  const rentalApplicationOccupants = rental.rental_application?.rental_application_occupants ?? []
  const legacyOccupants = rental.rental_occupants
  const occupants: NormalizedOccupant[] =
    rentalApplicationOccupants.length > 0
      ? rentalApplicationOccupants.map((occupant, index) => ({
          id: occupant.id,
          name: occupant.name,
          age: String(occupant.age),
          relation: occupant.relation,
          occupant_number: index + 1
        }))
      : legacyOccupants.map((occupant) => ({
          id: null,
          name: occupant.occupant_name ?? null,
          age: occupant.occupant_age ?? null,
          relation: occupant.occupant_relation ?? null,
          occupant_number: occupant.occupant_number ?? null
        }))
  // action button that copies tenant data to clipboard
  const copyTenantData = () => copyData([...sortedRentalUsers, ...occupants])

  const removeRentalUser = async (rentalUser: RentalUser) => {
    if (!onRefresh || !window.confirm('Remove this user from the rental?')) {
      return
    }

    setRemovingRentalUserId(rentalUser.id)

    try {
      await axios.delete(`/admin/rentals/${rental.id}/users/${rentalUser.id}`)
      await onRefresh()
    } catch (err: any) {
      window.alert(err.message)
    } finally {
      setRemovingRentalUserId(null)
    }
  }

  const promoteRentalUserToPrimary = async (rentalUser: RentalUser) => {
    if (!onRefresh || !window.confirm('Make this tenant the primary tenant?')) {
      return
    }

    setPromotingRentalUserId(rentalUser.id)

    try {
      await axios.put(`/admin/rentals/${rental.id}/users/${rentalUser.id}/primary`)
      await onRefresh()
    } catch (err: any) {
      window.alert(err.message)
    } finally {
      setPromotingRentalUserId(null)
    }
  }

  return (
    <>
      <ContentSectionCard
        padding
        title={'Tenants'}
        action={
          <HStack spacing={2}>
            {canAddTenant && (
              <Button
                size='xs'
                colorScheme='teal'
                onClick={(evt) => {
                  evt.stopPropagation()
                  setIsAddTenantModalOpen(true)
                }}
              >
                Add Tenant
              </Button>
            )}
            <Button
              size='xs'
              colorScheme='teal'
              onClick={(evt) => {
                evt.stopPropagation()
                copyTenantData()
              }}
            >
              Copy Tenants
            </Button>
          </HStack>
        }
      >
        <div>
          <List>
            {sortedRentalUsers.map((rentalUser) => {
              const { user, role, deactivated_at: deactivatedAt } = rentalUser
              return (
                <ListItem key={rentalUser.id} style={{ marginBottom: '.5rem' }}>
                  <HStack align='flex-start' justify='space-between'>
                    <VStack align='flex-start'>
                      <Link to={`/users/${user?.id}`} target='_blank'>
                        {!deactivatedAt && (
                          <b>
                            {user?.first_name} {user?.last_name} - {startCase(role)}
                          </b>
                        )}
                        {deactivatedAt && (
                          <s>
                            {user?.first_name} {user?.last_name} - {startCase(role)}
                          </s>
                        )}
                        <br />
                        {getContactInfo('email', user?.user_contact_details)}{' '}
                        {getContactInfo('phone', user?.user_contact_details)}
                      </Link>
                    </VStack>
                    {canManageTenants && !deactivatedAt && (
                      <Menu placement='bottom-end'>
                        <MenuButton
                          as={IconButton}
                          aria-label='Tenant actions'
                          icon={<BsThreeDotsVertical />}
                          size='xs'
                          variant='ghost'
                          onClick={(evt) => evt.stopPropagation()}
                        />
                        <MenuList minW='160px' p={1}>
                          {role !== 'primary' && (
                            <MenuItem
                              isDisabled={promotingRentalUserId === rentalUser.id}
                              onClick={(evt) => {
                                evt.stopPropagation()
                                promoteRentalUserToPrimary(rentalUser)
                              }}
                            >
                              Make Primary
                            </MenuItem>
                          )}
                          {role === 'primary' ? (
                            <Tooltip
                              label='Primary users cannot be removed from a rental'
                              placement='left'
                              shouldWrapChildren
                            >
                              <MenuItem isDisabled>Remove</MenuItem>
                            </Tooltip>
                          ) : (
                            <MenuItem
                              color='red.500'
                              isDisabled={removingRentalUserId === rentalUser.id}
                              onClick={(evt) => {
                                evt.stopPropagation()
                                removeRentalUser(rentalUser)
                              }}
                            >
                              Remove
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    )}
                  </HStack>
                </ListItem>
              )
            })}
            {occupants.map((occupant, index) => {
              const { id, occupant_number, name, relation } = occupant
              return (
                <ListItem key={id ?? index} style={{ marginBottom: '.5rem' }}>
                  <HStack justify='space-between'>
                    <VStack>
                      <b>
                        {name} - {'Occupant #'}
                        {occupant_number} - {relation}
                      </b>
                    </VStack>
                  </HStack>
                </ListItem>
              )
            })}
          </List>
        </div>
      </ContentSectionCard>
      {canManageTenants && onRefresh && (
        <AddTenantModal
          isOpen={isAddTenantModalOpen}
          onClose={() => setIsAddTenantModalOpen(false)}
          rentalId={rental.id}
          onRefresh={onRefresh}
        />
      )}
    </>
  )
}
